import { useState } from 'react';
import { Toggle } from 'components/Toggle';
import { DatedProductRule } from 'types/policy';
import { generateId } from 'utils/id';
import {
  DATED_PRODUCT_FIELD_LABELS,
  MAX_DATED_PRODUCT_RULES,
  describeDatedProductRule,
  getDatedProductRulesError,
  normalizeDatedProductRules,
} from './PolicySettings.datedRules';

type Props = {
  rules: DatedProductRule[];
  tags: { id: string; label: string }[];
  catalogReady: boolean;
  disabled?: boolean;
  loadError?: string | null;
  onChange: (rules: DatedProductRule[]) => void;
};

const blankRule = (): DatedProductRule => ({
  id: '',
  name: '',
  enabled: true,
  field: 'PRODUCT_TAG',
  operator: 'EQUALS',
  value: '',
  purchaseScope: 'ALL_PURCHASES',
  startDate: '',
  endDate: '',
  timeZone: 'UTC',
});
const inputClass =
  'mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50';
const secondaryButtonClass =
  'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50';

export const DatedProductRulesEditor = ({
  rules,
  tags,
  catalogReady,
  disabled,
  loadError,
  onChange,
}: Props) => {
  const [draft, setDraft] = useState<DatedProductRule>(blankRule);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [error, setError] = useState('');
  const reset = () => {
    setDraft(blankRule());
    setEditingIndex(null);
    setError('');
  };
  const update = (fields: Partial<DatedProductRule>) => {
    setDraft((current) => ({ ...current, ...fields }));
    setError('');
  };
  const atLimit =
    editingIndex === null && rules.length >= MAX_DATED_PRODUCT_RULES;
  const saveRule = () => {
    if (disabled || atLimit) return;
    const candidate = normalizeDatedProductRules([
      { ...draft, id: draft.id || generateId() },
    ])[0];
    const validationError = getDatedProductRulesError([candidate]);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (
      candidate.field === 'PRODUCT_TAG' &&
      (!catalogReady ||
        !tags.some(
          (tag) => tag.id.toLocaleLowerCase('en-US') === candidate.value,
        ))
    ) {
      setError('Choose a product tag from the synced Shopify catalog.');
      return;
    }
    onChange(
      editingIndex === null
        ? [...rules, candidate]
        : rules.map((rule, index) =>
            index === editingIndex ? candidate : rule,
          ),
    );
    reset();
  };

  return (
    <fieldset
      disabled={disabled}
      className="space-y-4 rounded-lg border border-gray-200 bg-white px-4 py-4"
    >
      <div>
        <h4 className="text-sm font-semibold text-gray-900">
          Product exclusion rules
        </h4>
        <p className="mt-1 text-xs leading-5 text-gray-500">
          Exclude matching items for all purchases or a specific purchase period.
          Other items in the order keep their usual eligibility.
        </p>
        <p className="mt-1 text-xs leading-5 text-gray-500">
          These rules are added to the exclusions above. A product tag used here
          does not need to be added as an unconditional exclusion.
        </p>
      </div>
      {loadError && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          <p>{loadError}</p>
          <button
            type="button"
            className="mt-2 font-semibold underline"
            onClick={() => {
              onChange([]);
              reset();
            }}
          >
            Reset invalid product rules
          </button>
        </div>
      )}
      {rules.length > 0 ? (
        <ul
          aria-label="Product exclusion rules"
          className="divide-y divide-gray-200 rounded-md border border-gray-200"
        >
          {rules.map((rule, index) => (
            <li key={`${rule.id}-${index}`} className="space-y-3 px-3 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="break-words text-sm font-semibold text-gray-900">
                    {rule.name || 'Invalid saved rule'}
                  </p>
                  <p className="mt-1 break-words text-xs leading-5 text-gray-600">
                    {describeDatedProductRule(rule)}
                  </p>
                </div>
                <Toggle
                  isEnabled={rule.enabled === true}
                  disabled={disabled}
                  ariaLabel={`Enable product rule ${rule.name}`}
                  onChange={(enabled) =>
                    onChange(
                      rules.map((current, currentIndex) =>
                        index === currentIndex
                          ? { ...current, enabled }
                          : current,
                      ),
                    )
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`mr-auto text-xs font-medium ${
                    rule.enabled ? 'text-blue-700' : 'text-gray-500'
                  }`}
                >
                  {rule.enabled
                    ? 'Enabled when product exclusions are active'
                    : 'Disabled'}
                </span>
                <button
                  type="button"
                  aria-label={`Edit ${rule.name}`}
                  className={secondaryButtonClass}
                  onClick={() => {
                    setDraft({ ...rule });
                    setEditingIndex(index);
                    setError('');
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${rule.name}`}
                  className={secondaryButtonClass}
                  onClick={() => {
                    onChange(
                      rules.filter((_, currentIndex) => index !== currentIndex),
                    );
                    reset();
                  }}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-500">
          No product exclusion rules added.
        </p>
      )}
      <div className="grid gap-4 border-t border-gray-100 pt-4 sm:grid-cols-2">
        <label className="text-sm font-medium text-gray-700">
          Rule name
          <input
            type="text"
            value={draft.name}
            maxLength={100}
            className={inputClass}
            placeholder="Outlet sale"
            onChange={(event) => update({ name: event.target.value })}
          />
        </label>
        <label className="text-sm font-medium text-gray-700">
          Product field
          <select
            value={draft.field}
            className={inputClass}
            onChange={(event) =>
              update({
                field: event.target.value as DatedProductRule['field'],
                value: '',
                operator: 'EQUALS',
              })
            }
          >
            {Object.entries(DATED_PRODUCT_FIELD_LABELS).map(
              ([field, label]) => (
                <option key={field} value={field}>
                  {label}
                </option>
              ),
            )}
          </select>
        </label>
        {draft.field === 'PRODUCT_TAG' ? (
          <label className="text-sm font-medium text-gray-700">
            Product tag
            <select
              value={draft.value}
              disabled={!catalogReady}
              className={inputClass}
              onChange={(event) => update({ value: event.target.value })}
            >
              <option value="">Select a Shopify product tag</option>
              {draft.value &&
                !tags.some(
                  (tag) => tag.id.toLocaleLowerCase('en-US') === draft.value,
                ) && (
                  <option value={draft.value}>Saved tag: {draft.value}</option>
                )}
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id.toLocaleLowerCase('en-US')}>
                  {tag.label}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="text-sm font-medium text-gray-700">
            {draft.field === 'SKU' ? 'SKU' : 'Product name'}
            <input
              type="text"
              value={draft.value}
              maxLength={255}
              className={inputClass}
              onChange={(event) => update({ value: event.target.value })}
            />
          </label>
        )}
        {draft.field === 'PRODUCT_NAME' ? (
          <label className="text-sm font-medium text-gray-700">
            Name match
            <select
              value={draft.operator}
              className={inputClass}
              onChange={(event) =>
                update({
                  operator: event.target.value as DatedProductRule['operator'],
                })
              }
            >
              <option value="EQUALS">Exactly matches</option>
              <option value="CONTAINS">Contains</option>
            </select>
          </label>
        ) : (
          <p className="self-end pb-2 text-xs text-gray-500">
            Tags and SKUs match exactly, ignoring capitalization.
          </p>
        )}
        <label className="text-sm font-medium text-gray-700">
          Purchase scope
          <select value={draft.purchaseScope || 'DATE_RANGE'} className={inputClass}
            onChange={event => update(event.target.value === 'ALL_PURCHASES'
              ? {purchaseScope: 'ALL_PURCHASES', startDate: '', endDate: '', timeZone: 'UTC'}
              : {purchaseScope: 'DATE_RANGE', startDate: '', endDate: '', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'})}>
            <option value="ALL_PURCHASES">All purchases</option>
            <option value="DATE_RANGE">Purchase period</option>
          </select>
        </label>
        {draft.purchaseScope !== 'ALL_PURCHASES' && <>
        <label className="text-sm font-medium text-gray-700">
          First purchase date
          <input
            type="date"
            value={draft.startDate}
            className={inputClass}
            onChange={(event) => update({ startDate: event.target.value })}
          />
        </label>
        <label className="text-sm font-medium text-gray-700">
          Last purchase date
          <input
            type="date"
            value={draft.endDate}
            className={inputClass}
            onChange={(event) => update({ endDate: event.target.value })}
          />
        </label>
        <label className="text-sm font-medium text-gray-700">
          Rule timezone
          <input
            type="text"
            value={draft.timeZone}
            className={inputClass}
            placeholder="America/Los_Angeles"
            list="dated-rule-timezones"
            onChange={(event) => update({ timeZone: event.target.value })}
          />
          <datalist id="dated-rule-timezones">
            {[
              'America/Los_Angeles',
              'America/Denver',
              'America/Chicago',
              'America/New_York',
              'Europe/London',
              'UTC',
            ].map((zone) => (
              <option key={zone} value={zone} />
            ))}
          </datalist>
        </label>
        <p className="self-end pb-2 text-xs leading-5 text-gray-500">
          Both dates include the full day in the selected timezone. Confirm the
          timezone matches the sale. Product names match without regard to
          capitalization.
        </p>
        <p className="text-xs text-gray-500">Uses the date the order was placed.</p>
        </>}
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {atLimit && (
        <p role="status" className="text-xs text-amber-800">
          You can add up to {MAX_DATED_PRODUCT_RULES} product rules.
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || atLimit}
          onClick={saveRule}
          className="rounded-md bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
        >
          {editingIndex === null ? 'Add product rule' : 'Update product rule'}
        </button>
        {editingIndex !== null && (
          <button
            type="button"
            onClick={reset}
            className={secondaryButtonClass}
          >
            Cancel edit
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Add or update the rule, then save the policy to apply your changes.
      </p>
    </fieldset>
  );
};
